import { ArrowRight, Bell, CheckCircle2, FileText, RefreshCw, Send } from 'lucide-react';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

import { KpiCard } from '@/components/sales/KpiCard';
import { SalesTableState } from '@/components/sales/SalesTableState';
import { SectionCard } from '@/components/sales/SectionCard';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useAuth from '@/hooks/useAuth';
import { useBillingDashboard, useBillingMetrics } from '@/hooks/useBilling';
import usePersistedState from '@/hooks/usePersistedState';
import { inr } from '@/lib/sales-metrics';
import type {
  BillingActivity,
  BillingDashboardPeriod,
  BillingDashboardScope,
  BillingFunnel,
} from '@/services/billing.service';
import { ROUTES } from '@/utils/constants';

// Single top filter: each preset maps to a (scope, period) pair sent to the
// server. "My Records" scopes the analytics to the viewer's claimed tasks; the
// rest are team-wide time windows.
type PresetKey = 'all' | 'mine' | 'today' | 'week' | 'month';
interface PresetDef {
  key: PresetKey;
  label: string;
  scope: BillingDashboardScope;
  period: BillingDashboardPeriod;
}
const PRESETS: PresetDef[] = [
  {
    key: 'all',
    label: 'All',
    scope: 'all',
    period: 'all',
  },
  {
    key: 'mine',
    label: 'My Records',
    scope: 'mine',
    period: 'all',
  },
  {
    key: 'today',
    label: 'Today',
    scope: 'all',
    period: 'today',
  },
  {
    key: 'week',
    label: 'This Week',
    scope: 'all',
    period: 'week',
  },
  {
    key: 'month',
    label: 'This Month',
    scope: 'all',
    period: 'month',
  },
];

// Assignment filter for the Action Required table (claim-based, ADR-043).
type ArFilter = 'all' | 'mine' | 'unassigned';
const AR_FILTERS: { key: ArFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'My Work' },
  { key: 'unassigned', label: 'Unassigned' },
];

// Workflow funnel bars, in order. Each in-billing lead is counted once, in its
// CURRENT stage (not cumulative). Colors mirror the billing workflow step dots.
const FUNNEL: { key: keyof BillingFunnel; label: string; color: string }[] = [
  { key: 'verification', label: 'Billing Verification', color: '#2f7ff0' },
  { key: 'invoiceGeneration', label: 'Invoice Generation', color: '#f28b82' },
  { key: 'serviceNotification', label: 'Service Team Notification', color: '#f6c453' },
  { key: 'invoiceSent', label: 'Invoice Sent', color: '#5b6b8c' },
  { key: 'partiallyBilled', label: 'Partially Billed', color: '#8b78e6' },
  { key: 'completed', label: 'Billing Completed', color: '#22c39a' },
];

const ACTIVITY_ICON: Record<BillingActivity['kind'], ComponentType<{ className?: string }>> = {
  verified: CheckCircle2,
  invoice: FileText,
  notify: Bell,
  sent: Send,
  completed: CheckCircle2,
};

const billingTone = (s: string): ChipTone => {
  if (s === 'Billing Complete' || s === 'Partially Billed') return 'success';
  if (s === 'Pending Verification') return 'warning';
  return 'info';
};

const pendingLabelFor = (iso: string | null): string => {
  if (!iso) return '-';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '-';
  const days = Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
  if (days === 0) return 'Today';
  return `${days} day${days === 1 ? '' : 's'}`;
};

const relativeTime = (iso: string): string => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const fmtDays = (v: number | null): string => (v == null ? '-' : `${v} day${v === 1 ? '' : 's'}`);
const fmtHrs = (v: number | null): string => (v == null ? '-' : `${v} hrs`);

const BillingDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const meId = user?.id ?? 0;
  const [preset, setPreset] = usePersistedState<PresetKey>('billingDashboard:preset', 'all');
  const [arFilter, setArFilter] = usePersistedState<ArFilter>('billingDashboard:arFilter', 'all');

  const active = PRESETS.find((p) => p.key === preset) ?? PRESETS[0];
  const metricsQuery = useBillingMetrics();
  const dashboardQuery = useBillingDashboard({ scope: active.scope, period: active.period });

  const metrics = metricsQuery.data;
  const dashboard = dashboardQuery.data;
  const isFetching = metricsQuery.isFetching || dashboardQuery.isFetching;
  const refetchAll = () => {
    void metricsQuery.refetch();
    void dashboardQuery.refetch();
  };

  const goQueue = () => {
    void navigate(ROUTES.BILLING_CLIENTS);
  };
  // Per ORDER, matching the queue (ADR-048) - a client can appear twice.
  const goWorkflow = (rid: number, orderId: number) => {
    void navigate(ROUTES.BILLING_WORKFLOW(rid, orderId));
  };

  const widgets: {
    label: string;
    value: number;
    tone: 'warning' | 'info' | 'success' | 'destructive';
  }[] = [
    { label: 'Pending Verification', value: metrics?.pendingVerification ?? 0, tone: 'warning' },
    { label: 'Invoice Pending', value: metrics?.invoicePending ?? 0, tone: 'warning' },
    {
      label: 'Service Team Pending',
      value: metrics?.serviceNotificationPending ?? 0,
      tone: 'info',
    },
    { label: 'Client Invoice Pending', value: metrics?.clientInvoicePending ?? 0, tone: 'info' },
    { label: 'Partially Billed', value: metrics?.partiallyBilled ?? 0, tone: 'success' },
    { label: 'Billing Complete', value: metrics?.completed ?? 0, tone: 'success' },
  ];

  const funnel = dashboard?.funnel;
  const funnelMax = funnel ? Math.max(1, ...FUNNEL.map((f) => funnel[f.key])) : 1;

  const perf = dashboard?.performance;
  const perfTiles: { label: string; value: string }[] = [
    { label: 'Avg Billing Completion', value: fmtDays(perf?.avgBillingCompletionDays ?? null) },
    { label: 'Avg Invoice Generation', value: fmtHrs(perf?.avgInvoiceGenerationHrs ?? null) },
    { label: 'Avg Service Handover', value: fmtHrs(perf?.avgServiceHandoverHrs ?? null) },
    {
      label: 'Avg Client Invoice Delivery',
      value: fmtHrs(perf?.avgClientInvoiceDeliveryHrs ?? null),
    },
    { label: 'Billing Success Rate', value: perf ? `${perf.billingSuccessRate}%` : '-' },
  ];

  const allActionRows = metrics?.actionRequired ?? [];
  const actionRows = allActionRows.filter((r) => {
    if (arFilter === 'mine') return r.assignedToId === meId;
    if (arFilter === 'unassigned') return r.assignedToId == null;
    return true;
  });

  const activities = dashboard?.recentActivities ?? [];
  const revenue = dashboard?.revenue ?? [];

  const metricsLoading = metricsQuery.isLoading;
  const dashLoading = dashboardQuery.isLoading;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground">Billing Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Live view of the billing queue handed over by Sales RM.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refetchAll} disabled={isFetching}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {widgets.map((w) => (
          <KpiCard
            key={w.label}
            label={w.label}
            value={metricsLoading ? '-' : w.value}
            tone={w.tone}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card padding="none" className="border-border/60">
          <CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              In Billing
            </p>
            <p className="mt-2 text-2xl font-extrabold tabular-nums">
              {metricsLoading ? '-' : (metrics?.total ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card padding="none" className="border-border/60">
          <CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Collectable
            </p>
            <p className="mt-2 text-2xl font-extrabold tabular-nums">
              {metricsLoading ? '-' : inr(metrics?.totalCollectable ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card padding="none" className="border-border/60">
          <CardContent className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Collected
            </p>
            <p className="mt-2 text-2xl font-extrabold tabular-nums text-success">
              {metricsLoading ? '-' : inr(metrics?.totalCollected ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Action Required (claim-aware filter) - kept above the analytics charts */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-bold text-foreground">Action Required</h2>
            <p className="text-xs text-muted-foreground">
              Oldest handed-over clients still awaiting a billing action.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5">
              {AR_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setArFilter(f.key)}
                  className={
                    arFilter === f.key
                      ? 'rounded-md bg-card px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm'
                      : 'rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground'
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={goQueue}>
              View queue
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {metricsQuery.isError && (
          <SalesTableState
            variant="error"
            title="Couldn't load billing metrics"
            onRetry={() => {
              void metricsQuery.refetch();
            }}
            className="min-h-[240px]"
          />
        )}
        {!metricsQuery.isError && metricsLoading && (
          <div className="flex min-h-[240px] items-center justify-center py-14 text-sm text-muted-foreground">
            Loading…
          </div>
        )}
        {!metricsQuery.isError && !metricsLoading && actionRows.length === 0 && (
          <SalesTableState
            variant="empty"
            title="Nothing needs action"
            description={
              arFilter === 'mine'
                ? 'You have no claimed tasks awaiting action.'
                : 'Every handed-over client has been billed.'
            }
            className="min-h-[240px]"
          />
        )}
        {!metricsQuery.isError && !metricsLoading && actionRows.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>ARN</TableHead>
                  <TableHead>Billing Stage</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead>Pending Since</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {actionRows.map((r) => (
                  <TableRow
                    key={`${r.id}:${r.orderId}`}
                    className="cursor-pointer whitespace-nowrap"
                    onClick={() => goWorkflow(r.id, r.orderId)}
                  >
                    <TableCell className="font-medium">{r.leadCode ?? r.id}</TableCell>
                    <TableCell>
                      <p className="font-semibold">{r.clientName}</p>
                      <p className="text-xs text-muted-foreground">
                        {`Order #${r.orderNumber} · ${r.orderType} · ${r.email}`}
                      </p>
                    </TableCell>
                    <TableCell>{r.arn}</TableCell>
                    <TableCell>
                      <StatusChip tone={billingTone(r.billingStatus)}>{r.billingStatus}</StatusChip>
                    </TableCell>
                    <TableCell>
                      {r.assignedToId == null ? (
                        <span className="text-xs text-muted-foreground">Unassigned</span>
                      ) : (
                        <StatusChip tone={r.assignedToId === meId ? 'info' : 'neutral'}>
                          {r.assignedToId === meId ? 'You' : (r.assignedToName ?? 'Claimed')}
                        </StatusChip>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {inr(r.pendingAmount)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {pendingLabelFor(r.handedOverAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Analytics scope/period filter - applies ONLY to the graphs below
          (segmented control, design.md section 7). */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Analytics
        </p>
        <div className="inline-flex w-fit flex-wrap rounded-lg border border-border bg-muted/40 p-0.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPreset(p.key)}
              className={
                preset === p.key
                  ? 'rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground'
                  : 'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground'
              }
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Funnel + Performance side by side */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Billing Workflow Funnel - current-stage distribution (each bill once) */}
        <SectionCard title="Billing Workflow Funnel" className="h-full">
          {dashLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading funnel…</p>
          ) : (
            <div className="space-y-3 py-1">
              {FUNNEL.map((f) => {
                const value = funnel?.[f.key] ?? 0;
                return (
                  <div
                    key={f.key}
                    className="grid grid-cols-[120px_1fr_auto] items-center gap-2 sm:grid-cols-[150px_1fr_auto]"
                  >
                    <span
                      className="truncate text-xs font-semibold text-foreground"
                      title={f.label}
                    >
                      {f.label}
                    </span>
                    <div className="h-5 w-full overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${(value / funnelMax) * 100}%`, background: f.color }}
                      />
                    </div>
                    <span className="w-6 text-right text-sm font-extrabold tabular-nums text-foreground">
                      {value}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {/* Billing Performance Metrics */}
        <SectionCard title="Billing Performance Metrics" className="h-full">
          <div className="grid grid-cols-2 gap-3">
            {perfTiles.map((tile) => (
              <div key={tile.label} className="rounded-xl border border-border bg-background p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {tile.label}
                </p>
                <p className="mt-1.5 text-xl font-extrabold tabular-nums text-foreground">
                  {dashLoading ? '-' : tile.value}
                </p>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Recent Activities + Revenue Processed side by side */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Recent Billing Activities (team-wide, max 10) */}
        <SectionCard
          title="Recent Billing Activities"
          className="h-full"
          contentClassName="max-h-[420px] overflow-y-auto"
        >
          {dashLoading && (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading activity…</p>
          )}
          {!dashLoading && activities.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No billing activity yet.
            </p>
          )}
          {!dashLoading && activities.length > 0 && (
            <ul className="space-y-2">
              {activities.map((a) => {
                const Icon = ACTIVITY_ICON[a.kind];
                return (
                  <li
                    key={a.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2"
                  >
                    <div className="flex min-w-0 items-start gap-2.5">
                      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-foreground">
                          {a.label}
                          <span className="text-muted-foreground">{`  ·  ${a.clientName}`}</span>
                        </p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {a.byName ? `by ${a.byName}` : 'by system'}
                          {a.amount != null ? ` · ${inr(a.amount)}` : ''}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {relativeTime(a.at)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        {/* Revenue Processed (last 6 months) */}
        <SectionCard title="Revenue Processed" className="h-full">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Done</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {!dashLoading &&
                  revenue.map((row) => (
                    <TableRow key={row.month} className="whitespace-nowrap">
                      <TableCell className="font-semibold">{row.month}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.invoices}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {inr(row.billingAmount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-success">
                        {row.completed}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-warning">
                        {row.pending}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default BillingDashboardPage;
